const PART_SIZE = 24; // chunk size is 24 bits
const MAX = Math.pow(2, PART_SIZE);
const BIT_MASK = MAX - 1;
const PRIME = 16777213; // prime number closest to 2^24
const CHAR_SIZE = 5;

/**
 * Simple hashing implementation to produce a 120 bit hash. hashing is based on unicode characters, which
 * are assumed to be less than 24 bits. Internally, we store a 120 bit number in 5 24 bit chunks, with
 * an initial value of a prime number just below 2^120 (340282366920938463463374607431768211283). For each
 * character passed in, we take the state, multiply it by a prime number less than 24 bits (16777213) and
 * add the new character. This means that on average, for each update, 50% of the bits change.
 */
export default class Digest {
    private state: number[];
    
    constructor() {
         // Initial state is prime less thant 2^120 - 1329227995784915872903807060280344457
        this.state = [0xffffff, 0xffffff, 0xffffff, 0xffffff, 0xffff89];
    }
    
    update(obj: any): Digest {
        if (obj == null) {
            this.updateString("null");
        } else {
            var type = typeof obj;
            if (type === "object") {
                if (obj instanceof Array) {
                    this.updateString("[");
                    for (var i = 0; i < obj.length; i++) {
                        if (i) {
                            this.updateString(",");
                        }
                        this.update(obj[i]);
                    }
                    this.updateString("]");
                } else {
                    var keys = Object.keys(obj);
                    keys.sort(); // need to make sure the same object produces the same hash each time
                    this.updateString("{");
                    for (var i = 0; i < keys.length; i++) {
                        if (i) {
                            this.updateString(",");
                        }
                        var key = keys[i];
                        this.updateString("\"").updateString(key).updateString("\":");
                        this.update(obj[key]);
                    }
                    this.updateString("}");
                }
            } else if (type === "string") {
                this.updateString("\"").updateString(obj.toString()).updateString("\"");
            } else {
                this.updateString(obj.toString());
            }
        }
        return this;
    }
    
    /**
     * 
     * @param value 
     * @returns 
     * 
     */
    updateString(value: string): Digest {
        const len = value.length;
        for (let i = 0; i < len; i++) {
            const code = value.charCodeAt(i);
            this.multiplyAndAdd(this.state, PRIME, code);
        }

        return this;
    }
    
    /**
     * 
     * @returns 
     */
    finish(): string {
        const len = 24;
        const ret = new Array(len);
        let length = 0;
        let value = 0;
        let charIndex = len - 1;
        for (let i = this.state.length; i-- > 0; ) {
            value |= (this.state[i] << length);
            length += PART_SIZE;
            while (length >= CHAR_SIZE) {
                ret[charIndex--] = this.toBase32Char(value);
                length -= CHAR_SIZE;
                value >>= CHAR_SIZE;
            }
        }
        return ret.join("");
    }
    
    /**
     * 
     * @param value 
     * 
     * @returns 
     */
    private toBase32Char(value: number): string {
        value = value & 0x1F;
        if (value < 10) {
            value += 48; // 48 is char code for '0'
        } else {
            value += 65 - 10; // 65 is char code for 'A'
        }

        return String.fromCharCode(value);
    }
    
    /**
     * 
     * @param bigInt 
     * @param smallInt 
     * @param toAdd 
     * 
     * @returns 
     */
    private multiplyAndAdd(bigInt: number[], smallInt: number, toAdd: number): number {
        for (let i = bigInt.length; i-- > 0; ) {
            const rawResult = (bigInt[i] * smallInt) + toAdd;
            const result = rawResult & BIT_MASK;
            bigInt[i] = result;
            if (result === rawResult) {
                toAdd = 0; // there is nothing to carry over
            } else { // there is a part to carry over
                toAdd = parseInt(rawResult / MAX); //Cant use shift right because it fails if the number is greater than 32 bits
            }
        }
        return toAdd;
    }
}
